import { Response } from "@/types"

import { FetchMethod, useCodeerApi } from "../useCodeerApi"

export type HandoffNotificationMode =
  | "custom"
  | "workspace_default"
  | "disabled"
export type HandoffNotificationOverrideMode = "custom" | "disabled"

export interface NotificationRuleReferenceSchema {
  id: string
  name: string
  event_type: string
  conditions: Record<string, unknown> | null
  agent_id: string | null
  agent_name: string | null
  is_enabled: boolean
}

export interface NotificationDestinationSchema {
  id: string
  name: string
  destination_type: string
  masked_endpoint: string
  is_enabled: boolean
  is_configured: boolean
  is_ready: boolean
  verification_status: string
  verification_resend_seconds: number
  used_by: NotificationRuleReferenceSchema[]
  created_at: string
  updated_at: string
}

export interface NotificationDestination {
  id: string
  name: string
  destinationType: string
  maskedEndpoint: string
  isEnabled: boolean
  isConfigured: boolean
  isReady: boolean
  verificationStatus: string
  verificationResendAvailableAt: number | null
  usedBy: NotificationRuleReferenceSchema[]
  createdAt: string
  updatedAt: string
}

export interface CreateNotificationDestinationRequest {
  name: string
  destination_type?: string
  config?: Record<string, string>
  webhook_url?: string
}

export interface UpdateNotificationDestinationRequest {
  name?: string
  destination_type?: string
  config?: Record<string, string>
  webhook_url?: string
  is_enabled?: boolean
  acknowledge_impact?: boolean
}

export interface NotificationRuleSchema {
  id: string
  name: string
  event_type: string
  conditions: Record<string, unknown> | null
  is_enabled: boolean
  destination_ids: string[]
  effective_destination_ids: string[]
  created_at: string
  updated_at: string
}

export interface NotificationEventSchema {
  event_type: string
  allowed_destination_types: string[]
  condition_schema: string | null
  rules: NotificationRuleSchema[]
  agent_mode_summary: Record<string, number> | null
}

export interface CreateNotificationRuleRequest {
  name: string
  event_type: string
  conditions: Record<string, unknown> | null
  is_enabled: boolean
  destination_ids: string[]
}

export interface UpdateNotificationRuleRequest {
  name?: string
  conditions?: Record<string, unknown> | null
  is_enabled?: boolean
  destination_ids?: string[]
}

export interface AgentHandoffNotificationSettingSchema {
  mode: HandoffNotificationMode
  destination_ids: string[]
  effective_destination_ids: string[]
  workspace_default_destination_ids?: string[]
  workspace_default_available?: boolean
  allowed_destination_types?: string[]
}

export interface AgentHandoffNotificationSetting {
  mode: HandoffNotificationMode
  destinationIds: string[]
  effectiveDestinationIds: string[]
  workspaceDefaultDestinationIds: string[]
  workspaceDefaultAvailable: boolean
  allowedDestinationTypes: string[]
}

export interface WorkspaceAgentHandoffNotificationSettingSchema extends AgentHandoffNotificationSettingSchema {
  agent_id: string
  agent_name: string
}

export interface WorkspaceAgentHandoffNotificationSetting extends AgentHandoffNotificationSetting {
  agentId: string
  agentName: string
}

export interface UpdateAgentHandoffNotificationSettingRequest {
  mode: HandoffNotificationOverrideMode
  destination_ids: string[]
}

export const notificationDestinationResponseToDestination = (
  schema: NotificationDestinationSchema
): NotificationDestination => ({
  id: schema.id,
  name: schema.name,
  destinationType: schema.destination_type,
  maskedEndpoint: schema.masked_endpoint || "",
  isEnabled: schema.is_enabled,
  isConfigured: schema.is_configured,
  isReady: schema.is_ready ?? schema.is_enabled,
  verificationStatus: schema.verification_status || "not_required",
  verificationResendAvailableAt:
    schema.verification_resend_seconds > 0
      ? Date.now() + schema.verification_resend_seconds * 1000
      : null,
  usedBy: schema.used_by || [],
  createdAt: schema.created_at,
  updatedAt: schema.updated_at,
})

export const agentHandoffNotificationResponseToSetting = (
  schema: AgentHandoffNotificationSettingSchema
): AgentHandoffNotificationSetting => ({
  mode: schema.mode,
  destinationIds: schema.destination_ids || [],
  effectiveDestinationIds: schema.effective_destination_ids || [],
  workspaceDefaultDestinationIds:
    schema.workspace_default_destination_ids || [],
  workspaceDefaultAvailable: schema.workspace_default_available ?? false,
  allowedDestinationTypes: schema.allowed_destination_types || [],
})

export const workspaceAgentHandoffNotificationResponseToSetting = (
  schema: WorkspaceAgentHandoffNotificationSettingSchema
): WorkspaceAgentHandoffNotificationSetting => ({
  agentId: schema.agent_id,
  agentName: schema.agent_name,
  ...agentHandoffNotificationResponseToSetting(schema),
})

const workspaceNotificationsPath = (
  organizationId: string,
  workspaceId: string
) => `/organizations/${organizationId}/workspaces/${workspaceId}`

export const useGetNotificationDestinationsApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<NotificationDestinationSchema[]>>(
    `${workspaceNotificationsPath(
      organizationId,
      workspaceId
    )}/notification-destinations`,
    FetchMethod.Get
  )

export const useCreateNotificationDestinationApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<
    Response<NotificationDestinationSchema>,
    CreateNotificationDestinationRequest
  >(
    `${workspaceNotificationsPath(
      organizationId,
      workspaceId
    )}/notification-destinations`,
    FetchMethod.Post
  )

export const useUpdateNotificationDestinationApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<
    Response<NotificationDestinationSchema>,
    UpdateNotificationDestinationRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/notification-destinations/__placeholder__",
    FetchMethod.Put,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/notification-destinations/${params.destinationId}`
  )

export const useDeleteNotificationDestinationApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<unknown>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/notification-destinations/__placeholder__",
    FetchMethod.Delete,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/notification-destinations/${params.destinationId}`
  )

export const useTestNotificationDestinationApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<unknown>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/notification-destinations/__placeholder__/test",
    FetchMethod.Post,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/notification-destinations/${params.destinationId}/test`
  )

export const useSendNotificationDestinationVerificationApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<NotificationDestinationSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/notification-destinations/__placeholder__/email-verification/send",
    FetchMethod.Post,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/notification-destinations/${params.destinationId}/email-verification/send`
  )

export const useGetNotificationEventsApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<NotificationEventSchema[]>>(
    `${workspaceNotificationsPath(
      organizationId,
      workspaceId
    )}/notification-events`,
    FetchMethod.Get
  )

export const useCreateNotificationRuleApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<NotificationRuleSchema>, CreateNotificationRuleRequest>(
    `${workspaceNotificationsPath(
      organizationId,
      workspaceId
    )}/notification-rules`,
    FetchMethod.Post
  )

export const useUpdateNotificationRuleApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<NotificationRuleSchema>, UpdateNotificationRuleRequest>(
    "/organizations/__placeholder__/workspaces/__placeholder__/notification-rules/__placeholder__",
    FetchMethod.Put,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/notification-rules/${params.ruleId}`
  )

export const useDeleteNotificationRuleApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<unknown>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/notification-rules/__placeholder__",
    FetchMethod.Delete,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/notification-rules/${params.ruleId}`
  )

export const useGetAgentHandoffNotificationSettingApi = (
  organizationId: string,
  workspaceId: string,
  agentId: string
) =>
  useCodeerApi<Response<AgentHandoffNotificationSettingSchema>>(
    `${workspaceNotificationsPath(
      organizationId,
      workspaceId
    )}/agents/${agentId}/handoff-notification-settings`,
    FetchMethod.Get
  )

export const useGetWorkspaceAgentHandoffNotificationSettingsApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<WorkspaceAgentHandoffNotificationSettingSchema[]>>(
    `${workspaceNotificationsPath(
      organizationId,
      workspaceId
    )}/agent-handoff-notification-settings`,
    FetchMethod.Get
  )

export const useUpdateWorkspaceAgentHandoffNotificationSettingApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<
    Response<AgentHandoffNotificationSettingSchema>,
    UpdateAgentHandoffNotificationSettingRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/agents/__placeholder__/handoff-notification-settings",
    FetchMethod.Put,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/agents/${params.agentId}/handoff-notification-settings`
  )

export const useDeleteWorkspaceAgentHandoffNotificationSettingApi = (
  organizationId: string,
  workspaceId: string
) =>
  useCodeerApi<Response<unknown>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/agents/__placeholder__/handoff-notification-settings",
    FetchMethod.Delete,
    true,
    (params) =>
      `${workspaceNotificationsPath(
        organizationId,
        workspaceId
      )}/agents/${params.agentId}/handoff-notification-settings`
  )
